import React from "react";
import { FiChevronRight } from "react-icons/fi";
import { brl, num, dash, pct, compact } from "../utils/formatters";
import { LANC_MES, PLAN_TOTAL } from "../mockDataSemanal";
import HeroPanel from "./HeroPanel";
import CreditPanel from "./CreditPanel";
import BreakdownTable from "./BreakdownTable";

const sum = (a) => a.reduce((s, x) => s + x, 0);

/**
 * Acúmulo semana a semana até o mês — estética "iOS Fitness": linha fina
 * cumulativa com área em gradiente, pontos discretos por etapa e pílulas de
 * valor. Totalmente fluido (SVG em %, sem scroll horizontal).
 */
function StepFlow({ steps, totalNome }) {
  let cum = 0;
  const pts = [{ name: "Início", cum: 0, delta: null, tip: "Início do mês" }];
  steps.forEach((s) => {
    cum += s.v;
    pts.push({ name: s.name, cum, delta: s.v, tip: s.tip });
  });
  pts[pts.length - 1].name = totalNome;
  pts[pts.length - 1].isTotal = true;

  let lo = Math.min(0, ...pts.map((p) => p.cum));
  let hi = Math.max(0, ...pts.map((p) => p.cum));
  const pad = (hi - lo) * 0.2 || 1;
  hi += pad;
  lo = lo < 0 ? lo - pad * 0.5 : 0;

  const X = (i) => 5 + (90 / Math.max(pts.length - 1, 1)) * i;
  const Y = (v) => ((v - lo) / (hi - lo)) * 100;
  const last = pts.length - 1;

  const linePts = pts.map((p, i) => `${X(i)},${(100 - Y(p.cum)).toFixed(2)}`).join(" ");
  const areaPts = `${X(0)},100 ${linePts} ${X(last)},100`;

  return (
    <div className="stepflow">
      <svg className="sf-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
        <defs>
          <linearGradient id="sfAreaGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10b981" stopOpacity="0.26" />
            <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
          </linearGradient>
        </defs>
        <line
          x1="0" x2="100"
          y1={(100 - Y(0)).toFixed(2)} y2={(100 - Y(0)).toFixed(2)}
          className="sf-zero" vectorEffect="non-scaling-stroke"
        />
        <polygon points={areaPts} fill="url(#sfAreaGrad)" />
        <polyline
          points={linePts} fill="none" className="sf-line"
          vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round"
        />
      </svg>

      {pts.map((p, i) => (
        <React.Fragment key={i}>
          <span
            className={`sf-pill ${p.isTotal ? "tot" : p.delta != null && p.delta < 0 ? "down" : ""}`}
            style={{ left: `${X(i)}%`, bottom: `calc(${Y(p.cum).toFixed(2)}% + 14px)`, "--d": i }}
          >
            {compact(p.cum)}
          </span>
          <span
            className={`sf-dot ${p.isTotal ? "tot" : p.delta == null ? "base" : p.delta < 0 ? "down" : "up"}`}
            style={{ left: `${X(i)}%`, bottom: `${Y(p.cum).toFixed(2)}%`, "--d": i }}
            title={p.tip ? `${p.tip} · ${p.isTotal ? "Total " : ""}${brl(p.cum)}` : brl(p.cum)}
          />
        </React.Fragment>
      ))}

      <div className="sf-names">
        {pts.map((p, i) => (
          <div className="sf-col" key={i} style={{ left: `${X(i)}%` }}>
            <span className={`n ${p.isTotal ? "strong" : ""}`}>{p.name}</span>
            {p.delta != null && !p.isTotal && (
              <span className={`d ${p.delta < 0 ? "down" : "up"}`}>
                {p.delta >= 0 ? "+" : ""}{compact(p.delta)}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Decomposição do faturamento bruto — barra segmentada estilo
 * "Armazenamento do iPhone": checkpoints (bruto → líquido → após tributos),
 * barra única fina com os componentes e legenda com valor e %.
 */
function Bridge({ mes }) {
  const B = mes.bruto;
  const MT = mes.total;
  const L = LANC_MES;

  const segs = [
    { nome: "Líquido após tributos", v: mes.liq - MT, cls: "k-net",
      tip: `${pct((mes.liq - MT) / B)} do faturamento bruto` },
    { nome: "Tributos a recolher", v: MT, cls: "k-pay",
      tip: `${pct(MT / mes.liq)} do faturamento líquido` },
    { nome: "Devoluções de vendas", v: mes.dev, cls: "k-out",
      tip: `${pct(mes.dev / B)} do faturamento bruto` },
    { nome: "Ajuste da planilha", v: Math.abs(L.ajuste_liquido), cls: "k-adj",
      tip: "Diferença entre bruto menos devoluções e o líquido informado" },
  ];

  return (
    <div className="flowbox">
      <div className="flow-checks">
        <div className="fc">
          <span>Faturamento bruto</span>
          <b>{brl(B)}</b>
          <small>Vendas {brl(B - L.servicos)} · Serviços {brl(L.servicos)}</small>
        </div>
        <FiChevronRight className="fc-arrow" />
        <div className="fc">
          <span>Faturamento líquido</span>
          <b>{brl(mes.liq)}</b>
          <small>{pct(mes.liq / B)} do bruto</small>
        </div>
        <FiChevronRight className="fc-arrow" />
        <div className="fc strong">
          <span>Líquido após tributos</span>
          <b>{brl(mes.liq - MT)}</b>
          <small>{pct((mes.liq - MT) / B)} do bruto</small>
        </div>
      </div>

      <div className="flowbar">
        {segs.map((s) => (
          <div
            key={s.nome}
            className={`seg ${s.cls}`}
            style={{ width: `${((s.v / B) * 100).toFixed(3)}%` }}
            title={`${s.nome} · ${brl(s.v)} · ${s.tip}`}
          />
        ))}
      </div>

      <ul className="flow-legend">
        {segs.map((s) => (
          <li key={s.nome}>
            <span className="nm"><i className={s.cls} />{s.nome}</span>
            <span className="vl">{brl(s.v)}</span>
            <span className="pc">{pct(s.v / B)} do bruto</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Mês consolidado: hero + waterfall semana a semana + bridge bruto→líquido +
 * breakdown mensal + crédito aproveitado + lançamentos do mês + conferência.
 */
export default function MonthReport({ mes, periodos, nomes, ranges, temManual }) {
  const L = LANC_MES;
  const MT = mes.total;
  const WS = sum(periodos.map((p) => p.total));
  const LANC = MT - WS;

  const steps = periodos.map((m, i) => ({
    name: nomes[i],
    v: m.total,
    tip: `${nomes[i]} (${ranges[i]})`,
  }));
  steps.push({ name: "Lançamentos do mês", v: LANC });

  const pc = (a, b) => L[a] + L[b];
  const lanc = [
    ["Serviços e locação", "Faturamento sem nota na planilha de entrada", L.servicos, pc("pis_deb_serv", "cof_deb_serv")],
    ["ISS", "3% sobre serviços e locação, menos R$ 490", null, L.iss],
    ["Créditos de aluguel e energia", "PIS e COFINS sobre aluguel de R$ 50.000,00 e energia", null, -(pc("pis_cred_aluguel", "cof_cred_aluguel") + pc("pis_cred_energia", "cof_cred_energia"))],
    ["Ajustes digitados nas fórmulas", "Correções manuais da base de PIS e COFINS de vendas e compras", null, L.pis_deb_adj + L.cof_deb_adj - L.pis_cred_adj_rev - L.cof_cred_adj_rev],
    ["Devolução de compra", "Linha da planilha com colunas deslocadas", null, -(L.pis_cred_devcompra + L.cof_cred_devcompra + L.ipi_cred_devcompra)],
    ["Diferença do faturamento líquido", "Líquido da planilha menos bruto e devoluções", L.ajuste_liquido, 0],
  ];
  const ltot = sum(lanc.map((x) => x[3]));

  return (
    <>
      <HeroPanel m={mes} isMes />

      <div className="panel-card">
        <div className="panel-head">
          <div>
            <h5>Semana a semana até o mês consolidado</h5>
            <p className="panel-sub">
              A linha acumula o que cada semana acrescenta ao total a recolher.
              Semana com saldo credor puxa a linha para baixo. Cada ponto é a
              apuração própria da semana, antes de usar o saldo credor da
              anterior; o mês é apurado como um todo, igual à planilha.
            </p>
          </div>
        </div>
        <StepFlow steps={steps} totalNome="Mês consolidado" />
      </div>

      <div className="panel-card">
        <div className="panel-head">
          <div>
            <h5>Do faturamento bruto ao líquido após tributos</h5>
            <p className="panel-sub">Como o faturamento bruto se decompõe: devoluções, ajuste, tributos e o que sobra.</p>
          </div>
        </div>
        <Bridge mes={mes} />
        <p className="footnote">Não considera custos: o estoque final da planilha está em branco.</p>
      </div>

      <BreakdownTable p={mes} isMes />

      <div className="grid-2">
        <CreditPanel
          rows={mes.rows}
          sub="Parte do débito de cada tributo coberta por crédito no mês, já com os lançamentos manuais."
        />
        <div className="panel-card">
          <div className="panel-head">
            <div>
              <h5>Lançamentos do mês</h5>
              <p className="panel-sub">
                Valores digitados dentro das fórmulas da planilha, sem data de
                nota. Entram só no consolidado.
              </p>
            </div>
          </div>
          <div className="tablewrap">
            <table className="apura-table">
              <thead>
                <tr>
                  <th>Lançamento</th>
                  <th className="r">Faturamento</th>
                  <th className="r">Tributos a recolher</th>
                </tr>
              </thead>
              <tbody>
                {lanc.map((x) => (
                  <tr key={x[0]}>
                    <td>
                      {x[0]}
                      <span className="sub2">{x[1]}</span>
                    </td>
                    <td className="r">{x[2] == null ? "–" : num(x[2])}</td>
                    <td className="r">{dash(x[3])}</td>
                  </tr>
                ))}
                <tr className="row-total">
                  <td>Total dos lançamentos</td>
                  <td className="r">{num(L.servicos + L.ajuste_liquido)}</td>
                  <td className="r">{num(ltot)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="recon">
            Conferência com a planilha:{" "}
            {temManual
              ? `com lançamentos manuais preenchidos, o total de ${brl(MT)} deixa de coincidir com os ${brl(PLAN_TOTAL)} do arquivo de teste. `
              : `o painel soma ${brl(MT)} a recolher${Math.abs(MT - PLAN_TOTAL) < 0.05 ? `, o mesmo valor da planilha (${brl(PLAN_TOTAL)}). ` : `, contra ${brl(PLAN_TOTAL)} da planilha. `}`}
            O ICMS-ST e DIFAL de PE, MT e PB não são considerados, como na planilha.
          </p>
        </div>
      </div>
    </>
  );
}
